/**
 * The module registry (PPL-407) — one component per `HomeModuleType`, the
 * way `F8Card` maps card types. `HomeModuleView` renders a payload module
 * by type; a type this build does not know renders NOTHING, so a server
 * that ships a new module before the client does degrades to a shorter
 * desk rather than a crash (forward compatibility).
 *
 * `WelcomeBackModule` renders its `expandsInto` through `HomeModuleView`,
 * which is an import cycle with this file. It is safe: both sides are
 * function declarations (instantiated before any module body runs) and the
 * registry is only read at render time.
 */

import type { HomeModuleType } from '../../types/home';
import type { ModuleComponent, ModuleProps } from './moduleTypes';
import { ReviewModule } from './modules/ReviewModule';
import { TodayModule } from './modules/TodayModule';
import { DatesModule } from './modules/DatesModule';
import { CheckinModule } from './modules/CheckinModule';
import { ContinueModule } from './modules/ContinueModule';
import { MeetModule } from './modules/MeetModule';
import { ClearAirModule } from './modules/ClearAirModule';
import { RoomsModule } from './modules/RoomsModule';
import { CirclesModule } from './modules/CirclesModule';
import { PostsModule } from './modules/PostsModule';
import { WeekModule } from './modules/WeekModule';
import { MemoryModule } from './modules/MemoryModule';
import { SetupModule } from './modules/SetupModule';
import { WelcomeBackModule } from './modules/WelcomeBackModule';
import { TrialModule } from './modules/TrialModule';

export const moduleRegistry: Record<HomeModuleType, ModuleComponent> = {
  review: ReviewModule,
  today: TodayModule,
  dates: DatesModule,
  checkin: CheckinModule,
  continue: ContinueModule,
  meet: MeetModule,
  clear_air: ClearAirModule,
  rooms: RoomsModule,
  circles: CirclesModule,
  posts: PostsModule,
  week: WeekModule,
  memory: MemoryModule,
  setup: SetupModule,
  welcome_back: WelcomeBackModule,
  trial: TrialModule,
};

/** Registry lookup that tolerates a type the payload knows and this build does not. */
export function getModuleComponent(type: string): ModuleComponent | null {
  return Object.prototype.hasOwnProperty.call(moduleRegistry, type) ? moduleRegistry[type as HomeModuleType] : null;
}

export function isKnownModuleType(type: string): type is HomeModuleType {
  return getModuleComponent(type) !== null;
}

export function HomeModuleView({ module, onAction }: ModuleProps) {
  const Component = getModuleComponent(module.type);
  if (!Component) return null;
  return <Component module={module} onAction={onAction} />;
}
